// Shared section / stats / testimonials defaults + style maps
// Used by both Home.jsx (rendering) and Settings.jsx (editing)

export const SIZE_CLASSES = {
  S: "text-4xl sm:text-5xl",
  M: "text-5xl sm:text-6xl",
  L: "text-5xl sm:text-7xl",
  XL: "text-6xl sm:text-8xl",
};

export const COLOR_CLASSES = {
  white: "text-[#F4F0EA]",
  gold: "text-[#C5A059]",
  gray: "text-[#A39E93]",
};

export const SIZE_OPTIONS = [
  ["S", "Petit"],
  ["M", "Moyen"],
  ["L", "Grand"],
  ["XL", "Très grand"],
];

export const COLOR_OPTIONS = [
  ["white", "Blanc"],
  ["gold", "Doré"],
  ["gray", "Gris"],
];

// Ordered list — drives the Settings editor and the Home fallbacks
export const SECTIONS = [
  { key: "services", label: "Prestations", default: { eyebrow: "Nos prestations", title: "Le geste juste,|au prix juste.", size: "L", color: "white" } },
  { key: "gallery", label: "Galerie de styles", default: { eyebrow: "Galerie de styles", title: "Choisissez|votre look.", size: "L", color: "white" } },
  { key: "about", label: "Qui sommes-nous", default: { eyebrow: "Qui sommes-nous", title: "Une maison,|des artisans.", size: "M", color: "white" } },
  { key: "values", label: "Nos valeurs", default: { eyebrow: "Nos valeurs", title: "Ce qui nous guide.", size: "M", color: "white" } },
  { key: "commitment", label: "Notre engagement", default: { eyebrow: "Notre engagement", title: "Une promesse,|tenue à chaque rendez-vous.", size: "M", color: "white" } },
  { key: "testimonials", label: "Témoignages", default: { eyebrow: "Témoignages", title: "Ce qu'ils en disent.", size: "L", color: "white" } },
  { key: "contact", label: "Contact", default: { eyebrow: "Contact", title: "Venez nous voir.", size: "L", color: "white" } },
  { key: "hours", label: "Horaires", default: { eyebrow: "Horaires", title: "Ouverture.", size: "S", color: "white" } },
  { key: "cta", label: "Appel à l'action (bas de page)", default: { eyebrow: "", title: "Prêt pour la coupe ?", size: "L", color: "white" } },
];

export const STAT_DEFAULTS = [
  { num: "10+", label: "Années d'expérience" },
  { num: "12K+", label: "Clients servis" },
  { num: "3", label: "Barbiers experts" },
  { num: "4,9", label: "Note Google" },
];

export const TESTIMONIAL_DEFAULTS = [
  { name: "Lucas R.", text: "Coupe nette, ambiance soignée. Le rasage à l'ancienne est un vrai moment. Je n'irai plus ailleurs.", stars: 5 },
  { name: "Mehdi B.", text: "Pro de la première à la dernière minute. La taille de barbe est juste parfaite.", stars: 5 },
  { name: "Antoine D.", text: "Réservation en ligne ultra simple, et le résultat est à la hauteur. Que du bon.", stars: 5 },
];

// Merge stored section config over the code default
export function getSection(salon, key) {
  const def = SECTIONS.find((s) => s.key === key)?.default || {};
  const stored = (salon?.sections || {})[key] || {};
  return { ...def, ...stored };
}

// Build a fully-populated sections object (defaults merged with stored) for editing
export function buildSectionsForm(salon) {
  const out = {};
  SECTIONS.forEach((sec) => {
    out[sec.key] = { ...sec.default, ...((salon?.sections || {})[sec.key] || {}) };
  });
  return out;
}

// ---------------------------------------------------------------------------
// Rich page content (everything that used to be hardcoded in Home.jsx)
// Editable from Settings → "Contenu de la page". Stored under salon.content.
// ---------------------------------------------------------------------------
export const CONTENT_DEFAULTS = {
  hero_rating: "4,9 / 5 · 320 avis Google",
  service_images: [
    "https://images.pexels.com/photos/7518688/pexels-photo-7518688.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=800&w=800",
    "https://images.pexels.com/photos/32878107/pexels-photo-32878107.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=800&w=800",
    "https://images.pexels.com/photos/1453005/pexels-photo-1453005.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=800&w=800",
  ],
  gallery_intro: "Parcourez nos styles signature. Repérez celui qui vous parle, cliquez pour réserver la prestation associée.",
  gallery: [
    { name: "Fade dégradé", desc: "Dégradé net, transition fondue", img: "https://images.unsplash.com/photo-1599351431202-1e0f0137899a?auto=format&fit=crop&w=720&h=900&q=80", service: "Coupe cheveux" },
    { name: "Coupe ciseaux", desc: "Texture naturelle, mouvement", img: "https://images.unsplash.com/photo-1503951914875-452162b0f3f1?auto=format&fit=crop&w=720&h=900&q=80", service: "Coupe cheveux" },
    { name: "Buzz court", desc: "Net, brut, sans artifice", img: "https://images.unsplash.com/photo-1622286342621-4bd786c2447c?auto=format&fit=crop&w=720&h=900&q=80", service: "Coupe cheveux" },
    { name: "Pompadour", desc: "Volume travaillé, look classique", img: "https://images.unsplash.com/photo-1605497788044-5a32c7078486?auto=format&fit=crop&w=720&h=900&q=80", service: "Coupe cheveux" },
    { name: "Barbe sculptée", desc: "Contours précis à la lame", img: "https://imperial-beard.fr/wp-content/uploads/2026/03/barbe-ducktail-tendance.jpg", service: "Taille barbe" },
    { name: "Rasage traditionnel", desc: "Serviette chaude, blaireau, rasoir", img: "https://images.unsplash.com/photo-1517832606299-7ae9b720a186?auto=format&fit=crop&w=720&h=900&q=80", service: "Taille barbe" },
    { name: "Combo signature", desc: "Coupe + barbe, finition complète", img: "https://i.pinimg.com/1200x/e6/7e/df/e67edf40ebc7af94187f67fa7df7cb27.jpg", service: "Cheveux + barbe" },
    { name: "Cheveux longs", desc: "Structure et entretien", img: "https://images.unsplash.com/photo-1567894340315-735d7c361db0?auto=format&fit=crop&w=720&h=900&q=80", service: "Coupe cheveux" },
  ],
  about_image: "https://images.pexels.com/photos/36361319/pexels-photo-36361319.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1200",
  about_paragraphs: [
    "Barber Atelier est né d'une conviction simple : la coupe d'un homme mérite mieux que la précipitation. Fondé à Paris en 2024 par une équipe de barbiers passionnés, notre atelier perpétue les gestes patiemment transmis — serviette chaude, lame affûtée, huiles essentielles.",
    "Un fauteuil, un client, une attention exclusive. Pas de cadence imposée, pas de raccourci. Juste le temps qu'il faut pour bien faire.",
  ],
  values: [
    { num: "01", title: "Précision", text: "Chaque coupe est exécutée avec une exigence d'orfèvre. La ligne juste, le dégradé maîtrisé." },
    { num: "02", title: "Tradition", text: "Nous honorons les gestes du barbier d'autrefois. Rasoir, blaireau, serviette chaude — rien n'est négocié." },
    { num: "03", title: "Hygiène", text: "Outils stérilisés à chaque client, linges propres, espace désinfecté. La rigueur sanitaire est non-négociable." },
    { num: "04", title: "Écoute", text: "Avant le ciseau, la conversation. Comprendre votre style, votre quotidien, vos envies — c'est là que tout commence." },
  ],
  commitment_image: "https://images.unsplash.com/photo-1503951914875-452162b0f3f1?auto=format&fit=crop&w=900&h=1100&q=80",
  commitment_items: [
    { title: "Ponctualité garantie", desc: "Votre créneau est sacré. Pas d'attente, pas de retard." },
    { title: "Produits haut-de-gamme", desc: "Nous sélectionnons des marques artisanales européennes : huiles naturelles, baumes traditionnels, lames suisses." },
    { title: "Conseil personnalisé", desc: "Coupe adaptée à votre morphologie, votre style et votre routine d'entretien." },
    { title: "Satisfaction prioritaire", desc: "Une finition ne vous convient pas ? Nous reprenons sans frais, immédiatement." },
  ],
  commitment_tags: ["Rasage traditionnel", "Coupe ciseaux", "Tondeuse fade", "Soin barbe"],
  awards: ["Meilleur Barbier Paris 2024", "Top 10 Time Out Paris", "Prix de l'Artisanat"],
  testimonials_rating: "4,9 / 5 · 320 avis Google",
  cta_subtitle: "Réservez en 30 secondes. Pas de compte requis.",
  footer_copyright: "© {year} {name} — Tous droits réservés",
  footer_owner_link: "Espace propriétaire",
  // Mentions légales & RGPD (éditable depuis Paramètres)
  legal_company: "",
  legal_status: "",
  legal_siret: "",
  legal_capital: "",
  legal_vat: "",
  legal_director: "",
  legal_host: "Emergent (infrastructure cloud)",
  legal_host_details: "",
  dpo_email: "",
};

// Merge stored content over code defaults (only falls back when key is absent)
export function getContent(salon) {
  const stored = (salon && salon.content) || {};
  const out = {};
  Object.keys(CONTENT_DEFAULTS).forEach((k) => {
    const s = stored[k];
    out[k] = s === undefined || s === null ? CONTENT_DEFAULTS[k] : s;
  });
  return out;
}

// Deep-clone for editing (so the editor never mutates the defaults)
export function buildContentForm(salon) {
  return JSON.parse(JSON.stringify(getContent(salon)));
}
